/**
 * Landing page component for AskAsper
 * Shown when user first arrives (no messages yet)
 */

import React from 'react';
import { AgenticMainLayout, DEFAULT_QUICK_ACTIONS, QuickAction } from 'asper_design_library';
import type { SidebarThread } from '../services/chatHistoryService';
import { type QuestionBankCategory, toQuestionBankApiFormat } from '../services/questionBankService';
import type { UserProfile, CustomQuestionDisplay } from '../types';

interface LandingPageProps {
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
    onQuickActionClick?: (action: QuickAction) => void;
    isLoading?: boolean;
    /** List of conversation threads to display in the sidebar */
    threads?: SidebarThread[];
    /** Whether sidebar threads are loading */
    sidebarThreadsLoading?: boolean;
    /** Currently active thread ID */
    activeThreadId?: string;
    /** User profile for sidebar (overrides userName for display/avatar) */
    user?: UserProfile;
    /** Whether the sidebar is in collapsed (icon-only) mode when expanded */
    sidebarDefaultCollapsed?: boolean;
    /** Whether to hide the sidebar header (logo and title) */
    sidebarHideHeader?: boolean;
    onNewChat?: () => void;
    onThreadSelect?: (threadId: string) => void;
    onThreadEdit?: (threadId: string, newName?: string) => void | Promise<void>;
    onThreadDelete?: (threadId: string) => void | Promise<void>;
    /** Custom questions for the question bank */
    customQuestions?: CustomQuestionDisplay[];
    /** Question bank categories (bucketed by category from API) */
    questionBankCategories?: QuestionBankCategory[];
    onAddCustomQuestion?: (question: { id: string; text: string }) => void | Promise<void>;
    onEditCustomQuestion?: (
        question: { id?: string | number; text?: string; QuestionID?: number; Question?: string },
    ) => void | Promise<void>;
    onDeleteCustomQuestion?: (questionId: string) => void | Promise<void>;
}

export const LandingPage: React.FC<LandingPageProps> = ({
    searchValue = '',
    onSearchChange,
    onSearchSubmit,
    onQuickActionClick,
    isLoading = false,
    threads = [],
    sidebarThreadsLoading = false,
    activeThreadId,
    user,
    sidebarDefaultCollapsed = false,
    sidebarHideHeader = false,
    onNewChat,
    onThreadSelect,
    onThreadEdit,
    onThreadDelete,
    customQuestions,
    questionBankCategories,
    onAddCustomQuestion,
    onEditCustomQuestion,
    onDeleteCustomQuestion,
}) => {
    const handleQuickActionClick = (action: QuickAction) => {
        if (action.fullQuestion && onSearchChange && onSearchSubmit) {
            onSearchChange(action.fullQuestion);
            onSearchSubmit(action.fullQuestion);
        }
        onQuickActionClick?.(action);
    };

    const layoutProps = {
        quickActions: DEFAULT_QUICK_ACTIONS,
        searchValue,
        isLoading,
        onSearchChange,
        onSearchSubmit,
        onQuickActionClick: handleQuickActionClick,
        threads,
        threadsLoading: sidebarThreadsLoading,
        activeThreadId,
        userName: user?.name,
        sidebarDefaultCollapsed,
        sidebarHideHeader,
        onNewChat,
        onThreadSelect,
        onThreadEdit,
        onThreadDelete,
        disableNavbar: true,
        customQuestions,
        questionBankCategories: questionBankCategories?.length
            ? toQuestionBankApiFormat(questionBankCategories)
            : undefined,
        onSaveAsCustom: (value: string) => onAddCustomQuestion?.({ id: '', text: value }),
        onEditCustomQuestion,
        onDeleteCustomQuestion,
    };
    return <AgenticMainLayout {...(layoutProps as any)} />;
};
